import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { BellRinging, CheckCircle, ForkKnife, X, ArrowRight } from "@phosphor-icons/react";

/**
 * Full-screen new-order alert.
 *
 * Shows a stacked queue of unacknowledged paid orders. The sidebar layout
 * is responsible for playing the repeating beep while `orders.length > 0`.
 */
export default function NewOrderAlert({ orders, onDismiss, onDismissAll }) {
    const navigate = useNavigate();
    const current = orders[0];

    // Block page scroll while alert is on-screen.
    useEffect(() => {
        if (orders.length === 0) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prev;
        };
    }, [orders.length]);

    if (orders.length === 0) return null;

    const total = Number(current.total ?? 0).toFixed(2);
    const itemCount = Array.isArray(current.items)
        ? current.items.reduce((s, i) => s + (i.qty || 0), 0)
        : 0;

    const takeOrder = () => {
        onDismissAll();
        navigate("/admin/orders");
    };

    return (
        <AnimatePresence>
            <motion.div
                key="new-order-alert"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm grid place-items-center px-4"
                data-testid="new-order-alert"
            >
                <motion.div
                    initial={{ scale: 0.85, y: 30 }}
                    animate={{ scale: 1, y: 0 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                    transition={{ type: "spring", stiffness: 280, damping: 22 }}
                    className="w-full max-w-lg bg-white rounded-3xl overflow-hidden shadow-2xl"
                >
                    {/* Pulsing red header */}
                    <div className="relative bg-primary text-primary-foreground p-6 overflow-hidden">
                        <motion.div
                            animate={{ scale: [1, 1.4, 1], opacity: [0.4, 0, 0.4] }}
                            transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
                            className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white"
                        />
                        <div className="relative flex items-center gap-3">
                            <motion.span
                                animate={{ rotate: [-12, 12, -12] }}
                                transition={{ duration: 0.6, repeat: Infinity }}
                                className="grid place-items-center w-14 h-14 rounded-2xl bg-white text-primary"
                            >
                                <BellRinging size={28} weight="fill" />
                            </motion.span>
                            <div className="flex-1 min-w-0">
                                <p className="text-[10px] font-bold uppercase tracking-[0.3em] opacity-80">
                                    Nueva orden pagada
                                </p>
                                <h2 className="font-display font-black text-3xl tracking-tight leading-tight">
                                    ¡Pedido entrando!
                                </h2>
                            </div>
                            {orders.length > 1 && (
                                <span
                                    className="bg-white text-primary font-display font-black text-xl px-3 py-1 rounded-full"
                                    data-testid="alert-queue-count"
                                >
                                    +{orders.length - 1}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Order info */}
                    <div className="p-6 space-y-4">
                        <div className="flex items-baseline justify-between gap-4">
                            <div className="min-w-0">
                                <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                                    Cliente
                                </p>
                                <p className="font-display font-black text-2xl truncate">
                                    {current.customer_name || "Cliente"}
                                </p>
                            </div>
                            <div className="text-right">
                                <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                                    Total
                                </p>
                                <p className="font-display font-black text-3xl text-primary">
                                    ${total}
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 rounded-xl p-3">
                            <ForkKnife size={16} weight="fill" className="text-primary" />
                            {itemCount > 0
                                ? `${itemCount} item${itemCount > 1 ? "s" : ""} en el pedido`
                                : "Pedido en cocina"}
                            <span className="ml-auto text-xs font-mono">
                                #{(current.id || "").slice(0, 8)}
                            </span>
                        </div>

                        {Array.isArray(current.items) && current.items.length > 0 && (
                            <ul className="text-sm divide-y divide-border max-h-32 overflow-y-auto">
                                {current.items.slice(0, 4).map((it, i) => (
                                    <li key={i} className="flex justify-between py-1.5">
                                        <span className="truncate">{it.qty}× {it.name}</span>
                                        <span className="font-semibold text-muted-foreground whitespace-nowrap">
                                            ${Number(it.line_total ?? 0).toFixed(2)}
                                        </span>
                                    </li>
                                ))}
                                {current.items.length > 4 && (
                                    <li className="text-xs text-muted-foreground py-1.5 text-center">
                                        + {current.items.length - 4} más
                                    </li>
                                )}
                            </ul>
                        )}

                        <div className="flex gap-2 pt-2">
                            <button
                                data-testid="alert-take-order"
                                onClick={takeOrder}
                                className="flex-1 inline-flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full py-4 font-bold uppercase tracking-widest text-sm transition-all hover:-translate-y-0.5"
                            >
                                Tomar pedido
                                <ArrowRight size={16} weight="bold" />
                            </button>
                            <button
                                data-testid="alert-dismiss"
                                onClick={() => onDismiss(current.id)}
                                className="inline-flex items-center justify-center gap-2 border-2 border-border hover:border-foreground text-foreground rounded-full px-5 py-4 font-bold uppercase tracking-widest text-sm transition-all"
                                title="Silenciar y continuar"
                            >
                                <CheckCircle size={18} weight="bold" />
                            </button>
                        </div>

                        {orders.length > 1 && (
                            <button
                                onClick={onDismissAll}
                                data-testid="alert-dismiss-all"
                                className="block w-full text-xs uppercase tracking-widest font-bold text-muted-foreground hover:text-foreground transition"
                            >
                                Silenciar todas ({orders.length})
                            </button>
                        )}
                    </div>
                </motion.div>

                {/* tiny close in corner just in case */}
                <button
                    onClick={() => onDismiss(current.id)}
                    aria-label="Close"
                    className="absolute top-6 right-6 grid place-items-center w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white transition"
                >
                    <X size={18} weight="bold" />
                </button>
            </motion.div>
        </AnimatePresence>
    );
}
